import React, { useEffect, useState, useRef } from 'react';
import { CSVLink } from 'react-csv';
import DataTable from 'react-data-table-component';
import { DatePicker, Tooltip, Space, Tag, Select, Modal, Input, Button, Upload, Card, Row, Col, Statistic } from 'antd';
import {
    InboxOutlined, UploadOutlined, CheckCircleOutlined, CloseCircleOutlined,
    FileExcelOutlined, DownloadOutlined, FilterOutlined,
    SearchOutlined, ClearOutlined,
} from '@ant-design/icons';
import PropagateLoader from "react-spinners/PropagateLoader";
import { getUTCToLocalDateTimeFormat, toDayDate, queryDate } from '../../../Utils/function';
import { getRequest, postRequest, downloadRequest, postFormDataRequest } from './../../../Utils/AppApi';
import { useHistory } from "react-router-dom";
import { toast } from 'react-toastify';
import {
    GET_FUND_TRANSFER_ADMIN_LIST,
    POST_FUND_TRANSFER_BULK_REFUND,
    POST_FUND_TRANSFER_MANUAL_REFUND,
    POST_FUND_TRANSFER_UPDATE_STATUS,
    POST_FUND_TRANSFER_MARK_FAILED,
} from './../../../Utils/appConstants';
import { INRFormat } from './../../../Utils/Common';

const { Dragger } = Upload;

/* ============================================================
   MODERN STYLES (scoped) — same design language as AePS report
============================================================ */

const reportStyles = `
.aeps-page {
    padding: 16px 24px 40px;
    background: #f5f7fb;
    min-height: calc(100vh - 60px);
}
.aeps-page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 18px;
    flex-wrap: wrap;
    gap: 12px;
}
.aeps-page-title {
    font-size: 22px;
    font-weight: 700;
    color: #1f2a44;
    margin: 0;
    letter-spacing: 0.2px;
}
.aeps-page-title span { color: #e5484d; }
.aeps-page-subtitle {
    font-size: 13px;
    color: #6b7280;
    margin: 2px 0 0;
}

.aeps-card {
    background: #ffffff;
    border-radius: 14px;
    box-shadow: 0 1px 2px rgba(16,24,40,.04), 0 4px 16px rgba(16,24,40,.06);
    border: 1px solid #eef0f4;
    overflow: hidden;
}
.aeps-card + .aeps-card { margin-top: 18px; }

.aeps-card-head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 20px;
    border-bottom: 1px solid #f0f2f6;
    background: linear-gradient(180deg, #fbfcfe 0%, #ffffff 100%);
}
.aeps-card-head-title {
    font-size: 13px;
    font-weight: 600;
    color: #344054;
    text-transform: uppercase;
    letter-spacing: .6px;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
}
.aeps-card-head-title .anticon {
    color: #5851D2;
    font-size: 14px;
}

/* Filter grid: 6 cols; row1 = 6 fields; row2 = full-width right-aligned actions */
.aeps-filter-body { padding: 18px 20px 20px; }

.aeps-filter-grid {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 14px 16px;
    align-items: end;
}

.aeps-field { display: flex; flex-direction: column; min-width: 0; }
.aeps-field label {
    font-size: 11.5px;
    font-weight: 600;
    color: #667085;
    text-transform: uppercase;
    letter-spacing: .4px;
    margin-bottom: 6px;
}

.aeps-field .ant-picker,
.aeps-field .ant-select,
.aeps-field .aeps-input {
    width: 100%;
    height: 40px;
    border-radius: 10px;
    border: 1px solid #e4e7ec;
    background: #fff;
    transition: border-color .15s, box-shadow .15s;
    font-size: 13.5px;
}
.aeps-field .ant-picker { padding: 0 11px; }
.aeps-field .ant-picker input { font-size: 13.5px; }
.aeps-field .ant-select .ant-select-selector {
    height: 40px !important;
    border-radius: 10px !important;
    border: 1px solid #e4e7ec !important;
    padding: 0 11px !important;
}
.aeps-field .ant-select-single .ant-select-selector .ant-select-selection-item,
.aeps-field .ant-select-single .ant-select-selector .ant-select-selection-placeholder {
    line-height: 38px !important;
    font-size: 13.5px;
}
.aeps-field .ant-picker:hover,
.aeps-field .ant-select:hover .ant-select-selector,
.aeps-field .aeps-input:hover { border-color: #c7cbd3 !important; }
.aeps-field .ant-picker-focused,
.aeps-field .ant-select-focused .ant-select-selector,
.aeps-field .aeps-input:focus {
    border-color: #5851D2 !important;
    box-shadow: 0 0 0 3px rgba(88,81,210,.12) !important;
    outline: none;
}

.aeps-input {
    padding: 0 12px;
    color: #101828;
    outline: none;
}
.aeps-input::placeholder { color: #98a2b3; }

/* Actions row — full width, right-aligned */
.aeps-filter-grid .aeps-actions {
    grid-column: 1 / -1;
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    align-items: flex-end;
    gap: 10px;
    min-height: 40px;
    padding-top: 4px;
}

/* ── Buttons ─────────────────────────────────────────── */
.aeps-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    padding: 0 20px;
    border-radius: 10px;
    border: 1px solid transparent;
    font-size: 13.5px;
    font-weight: 600;
    cursor: pointer;
    transition: transform .08s, box-shadow .15s, background .15s, border-color .15s;
    white-space: nowrap;
}
.aeps-btn:active { transform: translateY(1px); }

.aeps-btn-primary {
    background: linear-gradient(180deg, #6b64e0 0%, #5851D2 100%);
    color: #fff;
    box-shadow: 0 2px 6px rgba(88,81,210,.35);
}
.aeps-btn-primary:hover { box-shadow: 0 4px 12px rgba(88,81,210,.45); }

.aeps-btn-ghost {
    background: #ffffff;
    color: #475467;
    border-color: #e4e7ec;
}
.aeps-btn-ghost:hover { background: #f9fafb; border-color: #cfd4dc; }

.aeps-btn-success {
    background: #ffffff;
    color: #0e9384;
    border-color: #b7e4dd;
}
.aeps-btn-success:hover { background: #f0fdfa; border-color: #0e9384; }

/* ── Table ───────────────────────────────────────────── */
.aeps-table-wrap { padding: 4px 6px 10px; }
.aeps-table-wrap .rdt_TableHeadRow {
    background: #f9fafb !important;
    border-bottom: 1px solid #eef0f4 !important;
    min-height: 46px !important;
}
.aeps-table-wrap .rdt_TableCol {
    font-size: 11.5px !important;
    font-weight: 700 !important;
    color: #475467 !important;
    text-transform: uppercase !important;
    letter-spacing: .4px !important;
}
.aeps-table-wrap .rdt_TableRow {
    border-bottom: 1px solid #f2f4f7 !important;
    min-height: 52px !important;
    transition: background .15s;
}
.aeps-table-wrap .rdt_TableRow:hover { background: #fafbff !important; }
.aeps-table-wrap .rdt_TableCell { font-size: 13px !important; color: #344054 !important; }
.aeps-table-wrap .rdt_Pagination {
    border-top: 1px solid #eef0f4 !important;
    background: #fff !important;
    min-height: 52px !important;
}

/* ── Responsive ── */
@media (max-width: 1023px) {
    .aeps-filter-grid {
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    }
    .aeps-filter-grid .aeps-actions {
        grid-column: 1 / -1;
        margin-top: 6px;
        padding-top: 16px;
        border-top: 1px dashed #eef0f4;
    }
}

@media (max-width: 640px) {
    .aeps-page { padding: 12px; }
    .aeps-filter-grid .aeps-actions { justify-content: center; }
    .aeps-filter-grid .aeps-actions .aeps-btn {
        flex: 1 1 auto;
        justify-content: center;
    }
}
`;

/* ============================================================ */

function FundTransferAdminReport() {
    const history = useHistory();
    const csvLink = useRef();
    const limit = 10;
    const dateFormat = 'DD-MM-YYYY';

    // ---- Table / fetch state ----
    const [isLoading, setLoading]           = useState(false);
    const [endDate, setEndDate]             = useState(toDayDate());
    const [startDate, setStartDate]         = useState(toDayDate());
    const [getData, setData]                = useState({ docs: [] });
    const [getTotalRecord, setTotalRecord]  = useState(0);
    const [page, setPage]                   = useState(1);
    const [isLimit, setLimit]               = useState(limit);

    // ---- Fetch driver ----
    const [fetchTrigger, setFetchTrigger]     = useState(0);
    const [tableResetKey, setTableResetKey]   = useState(0);

    // ---- Search fields ----
    const [searchUserId, setSearchUserId]                   = useState('');
    const [searchAccountNumber, setSearchAccountNumber]     = useState('');
    const [searchTransId, setSearchTransId]                 = useState('');
    const [searchCustomerId, setSearchCustomerId]           = useState('');
    const [searchStatus, setSearchStatus]                   = useState(null);
    const [csvData, setCsvData]                             = useState([]);

    // ---- Upload modal ----
    const [uploadModalVisible, setUploadModalVisible] = useState(false);
    const [uploading, setUploading]                   = useState(false);
    const [selectedFile, setSelectedFile]             = useState(null);
    const [uploadResult, setUploadResult]             = useState(null);
    const [uploadBatchId, setUploadBatchId]           = useState(null);
    const [fileList, setFileList]                     = useState([]);

    // ---- Refund modal ----
    const [refundModalVisible, setRefundModalVisible] = useState(false);
    const [refundRequestId, setRefundRequestId]       = useState(null);
    const [refundRemark, setRefundRemark]             = useState('');

    // ---- Bank Ref modal ----
    const [bankRefModalVisible, setBankRefModalVisible] = useState(false);
    const [bankRefRequestId, setBankRefRequestId]       = useState(null);
    const [bankRefNumber, setBankRefNumber]             = useState('');
    const [bankRefRemark, setBankRefRemark]             = useState('');

    // ---- Mark Failed modal ----
    const [failModalVisible, setFailModalVisible] = useState(false);
    const [failRequestId, setFailRequestId]       = useState(null);
    const [failRemark, setFailRemark]             = useState('');

    // ---- Helpers ----
    const objectToQueryString = (obj) => {
        const str = [];
        for (const p in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, p)) {
                str.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]));
            }
        }
        return str.join('&');
    };

    const formatDateForAPI = (date) => {
        if (!date) return '';
        const d = new Date(date);
        const year  = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day   = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // ---- Fetch current page ----
    const getDataReports = () => {
        const params = { page, limit: isLimit };

        if (startDate && endDate) {
            params.startDate = formatDateForAPI(startDate);
            params.endDate   = formatDateForAPI(endDate);
        }
        params.searchAccountNumber = searchAccountNumber || '';
        params.searchCustomerId    = searchCustomerId    || '';
        params.searchTransId       = searchTransId       || '';
        params.searchUserId        = searchUserId        || '';
        params.searchStatus        = searchStatus        || '';

        const queryString = objectToQueryString(params);
        setLoading(true);
        getRequest(`${GET_FUND_TRANSFER_ADMIN_LIST}?${queryString}`, history)
            .then((response) => {
                if (response.success === true && response.data) {
                    setData(response.data);
                    setTotalRecord(response.data.totalDocs || 0);
                    if (typeof response.data.page === 'number') setPage(response.data.page);
                } else {
                    setData({ docs: [] });
                    setTotalRecord(0);
                }
                setLoading(false);
            })
            .catch((error) => {
                console.log(error);
                setData({ docs: [] });
                setTotalRecord(0);
                setLoading(false);
            });
    };

    // ---- Single fetch driver ----
    useEffect(() => {
        getDataReports();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchTrigger]);

    // ---- Reset + refetch ----
    const resetToFirstPageAndFetch = () => {
        setPage(1);
        setTableResetKey((k) => k + 1);
        setFetchTrigger((k) => k + 1);
    };

    const handlePageChange = (newPage /*, totalRows */) => {
        setPage(newPage);
        setFetchTrigger((k) => k + 1);
    };

    const handleRowsPerPageChange = (newLimit) => {
        setLimit(newLimit);
        resetToFirstPageAndFetch();
    };

    const handleSearch = () => resetToFirstPageAndFetch();

    const handleClear = () => {
        setStartDate(toDayDate());
        setEndDate(toDayDate());
        setSearchAccountNumber('');
        setSearchTransId('');
        setSearchCustomerId('');
        setSearchUserId('');
        setSearchStatus(null);
        resetToFirstPageAndFetch();
    };

    // ============================================================
    //  CSV Export
    // ============================================================
    const getCSVDataList = () => {
        const params = { exportsCsv: true };
        if (startDate && endDate) {
            params.startDate = startDate ? queryDate(startDate) : '';
            params.endDate   = endDate   ? queryDate(endDate)   : '';
        }
        params.searchAccountNumber = searchAccountNumber || '';
        params.searchCustomerId    = searchCustomerId    || '';
        params.searchTransId       = searchTransId       || '';
        params.searchUserId        = searchUserId        || '';
        params.searchStatus        = searchStatus        || '';

        const queryString = objectToQueryString(params);
        getRequest(`${GET_FUND_TRANSFER_ADMIN_LIST}?${queryString}`, history)
            .then((response) => {
                if (response.success === true) {
                    const records = response.data || [];
                    const csvRows = records.map((row) => ({
                        'Date Time':       getUTCToLocalDateTimeFormat(row.createdAt),
                        'Transaction ID':  row.transactionId,
                        'Amount':          INRFormat(row.amount),
                        'Service Charge':  INRFormat(row.commissionData?.serviceCharge ?? 0),
                        'Total Debit':     INRFormat(row.commissionData?.totalDebit ?? row.amount),
                        'API Provider':    'YB',
                        'Final Status':    row.finalStatus,
                        'Workflow Status': row.workflowStatus,
                        'Level 1 Status':  row.level1Status,
                        'Level 2 Status':  row.level2Status,
                        'Mode':            'NEFT',
                        'Customer':        row.customerName || row.partnerName,
                        'Beneficiary':     row.baneName,
                        'Account Number':  row.accountNumber,
                        'IFSC':            row.ifsccode,
                        'Requester':       row.requester?.username || '',
                        'Checker':         row.checker?.username   || '',
                        'Maker':           row.maker?.username     || '',
                    }));
                    setCsvData(csvRows);
                    setTimeout(() => {
                        if (csvLink.current && csvLink.current.link) {
                            csvLink.current.link.click();
                        }
                    }, 500);
                } else {
                    setCsvData([]);
                    toast.error('No data to export');
                }
            })
            .catch((error) => {
                console.log(error);
                toast.error('Failed to export CSV');
            });
    };

    // ---- A2A DFT Export ----
    const handleExportA2A = async (status = null) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (startDate) params.append('startDate', formatDateForAPI(startDate));
            if (endDate)   params.append('endDate',   formatDateForAPI(endDate));
            if (status && typeof status === 'string') params.append('status', status);
            const url = `/fund-transfer/export-a2a-dft?${params.toString()}`;

            const response = await downloadRequest(url, history);
            const blob = response.data;
            if (blob.size === 0) throw new Error('Received empty file');

            const contentDisposition = response.headers['content-disposition'];
            let filename = 'A2A_DFT.xlsx';
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?([^"]+)"?/);
                if (match) filename = match[1];
            }
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
            toast.success('✅ File downloaded successfully');
        } catch (error) {
            console.error('💥 Export error:', error);
            toast.error(error.message || 'Failed to download A2A DFT');
        } finally {
            setLoading(false);
        }
    };

    // ---- Download Template ----
    const downloadTemplate = async () => {
        try {
            const response = await downloadRequest('/fund-transfer/download-template', history);
            const blob = response.data;
            const contentDisposition = response.headers['content-disposition'];
            let filename = 'status_update_template.xlsx';
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?([^"]+)"?/);
                if (match) filename = match[1];
            }
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
            toast.success('📥 Template downloaded');
        } catch (error) {
            toast.error('Failed to download template');
        }
    };

    // ---- Upload File ----
    const handleFileUpload = async () => {
        if (!selectedFile) {
            toast.error('Please select a file');
            return;
        }
        const formData = new FormData();
        formData.append('file', selectedFile, selectedFile.name);
        setUploading(true);
        try {
            const response = await postFormDataRequest('/fund-transfer/upload-status', formData, history);
            if (response.success) {
                toast.success(`Upload completed: ${response.data.success} success, ${response.data.failed} failed`);
                setUploadResult(response.data);
                setUploadBatchId(response.data.batchId);
                setFileList([]);
                setSelectedFile(null);
                getDataReports();
            } else {
                toast.error(response.message || 'Upload failed');
            }
        } catch (err) {
            console.error('Upload error:', err);
            toast.error('Upload failed');
        } finally {
            setUploading(false);
        }
    };

    // ---- Export Upload Batch ----
    const exportUploadBatch = async () => {
        if (!uploadBatchId) return;
        try {
            const response = await downloadRequest(`/fund-transfer/upload-batch/${uploadBatchId}`, history);
            const blob = response.data;
            const contentDisposition = response.headers['content-disposition'];
            let filename = `upload_batch_${uploadBatchId}.xlsx`;
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?([^"]+)"?/);
                if (match) filename = match[1];
            }
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
            toast.success('📊 Results exported');
        } catch (error) {
            toast.error('Failed to export batch results');
        }
    };

    // ---- Manual Refund ----
    const openRefundModal = (requestId) => {
        setRefundRequestId(requestId);
        setRefundRemark('');
        setRefundModalVisible(true);
    };

    const handleManualRefund = () => {
        if (!refundRequestId) return;
        setLoading(true);
        const payload = { requestId: refundRequestId, remark: refundRemark };
        postRequest(`${POST_FUND_TRANSFER_MANUAL_REFUND}`, payload, history)
            .then((response) => {
                if (response.success) {
                    toast.success(response.message);
                    setRefundModalVisible(false);
                    getDataReports();
                } else {
                    toast.error(response.message);
                }
                setLoading(false);
            })
            .catch((error) => { console.log(error); setLoading(false); });
    };

    // ---- Bank Ref (Mark Success) ----
    const openBankRefModal = (requestId) => {
        setBankRefRequestId(requestId);
        setBankRefNumber('');
        setBankRefRemark('');
        setBankRefModalVisible(true);
    };

    const handleBankRefSubmit = () => {
        if (!bankRefRequestId) return;
        if (!bankRefNumber) {
            toast.error('Bank reference number (UTR/RRN) is required');
            return;
        }
        setLoading(true);
        const payload = { requestId: bankRefRequestId, bank_ref_num: bankRefNumber, remark: bankRefRemark };
        postRequest(`${POST_FUND_TRANSFER_UPDATE_STATUS}`, payload, history)
            .then((response) => {
                if (response.success) {
                    toast.success(response.message);
                    setBankRefModalVisible(false);
                    getDataReports();
                } else {
                    toast.error(response.message);
                }
                setLoading(false);
            })
            .catch((error) => { console.log(error); setLoading(false); });
    };

    // ---- Mark Failed ----
    const openFailModal = (requestId) => {
        setFailRequestId(requestId);
        setFailRemark('');
        setFailModalVisible(true);
    };

    const handleFailSubmit = () => {
        if (!failRequestId) return;
        setLoading(true);
        const payload = { requestId: failRequestId, remark: failRemark };
        postRequest(`${POST_FUND_TRANSFER_MARK_FAILED}`, payload, history)
            .then((response) => {
                if (response.success) {
                    toast.success(response.message);
                    setFailModalVisible(false);
                    getDataReports();
                } else {
                    toast.error(response.message);
                }
                setLoading(false);
            })
            .catch((error) => { console.log(error); setLoading(false); });
    };

    // ---- Upload Dragger props ----
    const uploadProps = {
        name: 'file',
        multiple: false,
        fileList: fileList,
        beforeUpload: (file) => {
            setSelectedFile(file);
            setFileList([file]);
            return false;
        },
        onRemove: () => {
            setSelectedFile(null);
            setFileList([]);
        },
        accept: '.xls,.xlsx',
        maxCount: 1,
    };

    // ============================================================
    //  TABLE COLUMNS
    // ============================================================
    const columns = [
        { name: 'Date Time', selector: row => getUTCToLocalDateTimeFormat(row.createdAt), width: '180px' },
        {
            name: 'Trans ID',
            selector: row => <Tooltip placement="bottom" title={row.transactionId}>{row.transactionId}</Tooltip>,
            width: '180px',
        },
        {
            name: 'User Id / Name',
            selector: row => `${row.requester?.username || '-'} / ${row.requester?.name || '-'}`,
            width: '200px',
        },
        { name: 'Amount', selector: row => INRFormat(row.amount), width: '110px' },
        { name: 'Service Charge', selector: row => INRFormat(row.commissionData?.serviceCharge ?? 0), width: '130px' },
        { name: 'Total Debit', selector: row => INRFormat(row.commissionData?.totalDebit ?? row.amount), width: '130px' },
        { name: 'API Provider', selector: () => 'YB', width: '120px', center: true },
        { name: 'Txn Type', selector: row => row.txnType || '-', width: '150px' },
        {
            name: 'Final Status',
            selector: row => {
                const statusMap = {
                    'submitted': 'Submitted', 'in_process': 'In Process', 'sent_to_bank': 'Sent to Bank',
                    'pending_at_bank': 'Pending at Bank', 'success': 'Success', 'failed': 'Failed',
                    'refunded': 'Refunded',
                };
                return statusMap[row.finalStatus] || row.finalStatus || 'Unknown';
            },
            width: '140px',
        },
        { name: 'Level 1', selector: row => row.level1Status || '-', width: '110px' },
        { name: 'Level 2', selector: row => row.level2Status || '-', width: '110px' },
        { name: 'Mode', selector: () => 'NEFT', width: '80px' },
        { name: 'Mobile', selector: row => row.customerMobile || '-', width: '130px' },
        { name: 'IFSC', selector: row => row.ifsccode || '-', width: '130px' },
        { name: 'Account', selector: row => row.accountNumber || '-', width: '160px' },
        { name: 'Beneficiary', selector: row => row.baneName || '-', width: '160px' },
        { name: 'Bank RRN', selector: row => row.bank_ref_num || '-', width: '140px' },
        {
            name: 'Action',
            cell: row => {
                if ((row.workflowStatus === 'pending_at_bank' || row.workflowStatus === 'pending_maker') && !row.isRefund) {
                    return (
                        <Space size="middle" wrap>
                            <Tag color="blue" className="pointer" onClick={() => openBankRefModal(row._id)}>Mark Success</Tag>
                            <Tag color="red" className="pointer" onClick={() => openFailModal(row._id)}>Mark Failed</Tag>
                        </Space>
                    );
                } else if (row.workflowStatus === 'executed') {
                    return <Tag color="green">Success</Tag>;
                } else if (row.workflowStatus === 'failed' && !row.isRefund) {
                    return <Tag color="orange" className="pointer" onClick={() => openRefundModal(row._id)}>Refund</Tag>;
                }
                return <Tag color="grey">-</Tag>;
            },
            width: '200px',
        },
    ];

    // ============================================================
    //  RENDER
    // ============================================================
    return (
        <div className="aeps-page">
            <style>{reportStyles}</style>

            {/* ─── Page header ─────────────────────────────────── */}
            <div className="aeps-page-header">
                <div>
                    <h1 className="aeps-page-title">
                        Fund Transfer <span>Admin Report</span>
                    </h1>
                    <p className="aeps-page-subtitle">
                        Search, filter, and export fund transfer transactions
                    </p>
                </div>
            </div>

            {/* ─── Filters card ───────────────────────────────── */}
            <div className="aeps-card">
                <div className="aeps-card-head">
                    <h2 className="aeps-card-head-title">
                        <FilterOutlined /> Filters
                    </h2>
                </div>

                <div className="aeps-filter-body">
                    <div className="aeps-filter-grid">
                        <div className="aeps-field">
                            <label>From Date</label>
                            <DatePicker
                                value={startDate}
                                format={dateFormat}
                                onChange={(date) => setStartDate(date)}
                                placeholder="From date"
                            />
                        </div>

                        <div className="aeps-field">
                            <label>To Date</label>
                            <DatePicker
                                value={endDate}
                                format={dateFormat}
                                onChange={(date) => setEndDate(date)}
                                placeholder="To date"
                            />
                        </div>

                        <div className="aeps-field">
                            <label>User ID</label>
                            <input
                                type="text"
                                className="aeps-input"
                                placeholder="Enter User ID"
                                onChange={(e) => setSearchUserId(e.target.value)}
                                value={searchUserId}
                            />
                        </div>

                        <div className="aeps-field">
                            <label>Transaction ID</label>
                            <input
                                type="text"
                                className="aeps-input"
                                placeholder="Enter Transaction ID"
                                onChange={(e) => setSearchTransId(e.target.value)}
                                value={searchTransId}
                            />
                        </div>

                        <div className="aeps-field">
                            <label>Account Number</label>
                            <input
                                type="text"
                                className="aeps-input"
                                placeholder="Enter A/C number"
                                onChange={(e) => setSearchAccountNumber(e.target.value)}
                                value={searchAccountNumber}
                            />
                        </div>

                        <div className="aeps-field">
                            <label>Status</label>
                            <Select
                                value={searchStatus || undefined}
                                onChange={(value) => setSearchStatus(value)}
                                allowClear
                                placeholder="Select status"
                                options={[
                                    { value: 'submitted', label: 'Submitted' },
                                    { value: 'in_process', label: 'In Process' },
                                    { value: 'sent_to_bank', label: 'Sent to Bank' },
                                    { value: 'pending_at_bank', label: 'Pending at Bank' },
                                    { value: 'success', label: 'Success' },
                                    { value: 'failed', label: 'Failed' },
                                    { value: 'refunded', label: 'Refunded' },
                                    { value: 'pending_checker', label: 'Pending Checker' },
                                    { value: 'pending_maker', label: 'Pending Maker' },
                                ]}
                            />
                        </div>

                        {/* Actions — full-width row, right-aligned */}
                        <div className="aeps-actions">
                            <button type="button" className="aeps-btn aeps-btn-ghost" onClick={handleClear}>
                                <ClearOutlined /> Clear
                            </button>

                            <button type="button" className="aeps-btn aeps-btn-success" onClick={getCSVDataList}>
                                <DownloadOutlined /> Export CSV
                            </button>

                            <button type="button" className="aeps-btn aeps-btn-primary" onClick={handleSearch}>
                                <SearchOutlined /> Search
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ─── Table card ─────────────────────────────────── */}
            <div className="aeps-card">
                <div className="aeps-table-wrap">
                    <DataTable
                        key={tableResetKey}
                        title=""
                        columns={columns}
                        data={getData.docs || []}
                        highlightOnHover
                        pagination
                        paginationServer
                        paginationTotalRows={getTotalRecord}
                        progressPending={isLoading}
                        progressComponent={
                            <div className="text-center py-4">
                                <PropagateLoader color="#5851D2" size={20} />
                            </div>
                        }
                        onChangeRowsPerPage={handleRowsPerPageChange}
                        onChangePage={handlePageChange}
                        noDataComponent={
                            <div style={{ padding: '40px 20px', color: '#98a2b3', fontSize: 13 }}>
                                No records found for the selected filters.
                            </div>
                        }
                    />
                </div>
            </div>

            {/* hidden CSV trigger */}
            <CSVLink
                data={csvData}
                filename="fund_transfer_admin_report"
                className="hidden"
                ref={csvLink}
                target="_blank"
                style={{ display: 'none' }}
            />

            {/* ====== UPLOAD STATUS MODAL ====== */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <UploadOutlined style={{ color: '#1890ff', fontSize: '20px' }} />
                        <span style={{ fontSize: '18px', fontWeight: '600' }}>Upload Status Update</span>
                    </div>
                }
                visible={uploadModalVisible}
                onCancel={() => {
                    setUploadModalVisible(false);
                    setSelectedFile(null);
                    setUploadResult(null);
                    setUploadBatchId(null);
                    setFileList([]);
                }}
                footer={null}
                destroyOnClose
                width={650}
                bodyStyle={{ padding: '24px' }}
            >
                <div className="row">
                    <div className="col-md-12">
                        <div className="alert alert-info" style={{ borderRadius: '8px', background: '#e6f7ff', borderColor: '#91d5ff' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <FileExcelOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
                                <div>
                                    <strong style={{ display: 'block', color: '#000000' }}>Upload Excel file with transaction status</strong>
                                    <small className="text-muted">
                                        Columns: Customer Reference (Transaction ID), RBI/UTR Reference, Status (Accepted By Bank / Rejected)
                                    </small>
                                </div>
                            </div>
                        </div>

                        <div className="form-group" style={{ marginBottom: '20px' }}>
                            <Dragger {...uploadProps} style={{ padding: '20px 0', borderRadius: '8px' }}>
                                <p className="ant-upload-drag-icon">
                                    <InboxOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
                                </p>
                                <p className="ant-upload-text" style={{ fontSize: '16px', fontWeight: '500' }}>
                                    Click or drag file to this area to upload
                                </p>
                                <p className="ant-upload-hint" style={{ color: '#888' }}>
                                    Supports .xls and .xlsx (Max 10MB)
                                </p>
                                {selectedFile && (
                                    <div style={{ marginTop: '12px', background: '#f0f5ff', padding: '8px 16px', borderRadius: '4px', display: 'inline-block' }}>
                                        <FileExcelOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
                                        <strong>{selectedFile.name}</strong> ({(selectedFile.size / 1024).toFixed(1)} KB)
                                    </div>
                                )}
                            </Dragger>
                        </div>

                        <div className="mt-3" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <Button
                                type="primary"
                                onClick={handleFileUpload}
                                loading={uploading}
                                disabled={!selectedFile}
                                icon={<UploadOutlined />}
                                size="large"
                                style={{ flex: 1, minWidth: '120px' }}
                            >
                                Upload
                            </Button>
                            <Button onClick={downloadTemplate} icon={<DownloadOutlined />} size="large">
                                Download Template
                            </Button>
                            <Button
                                onClick={() => {
                                    setUploadModalVisible(false);
                                    setSelectedFile(null);
                                    setUploadResult(null);
                                    setUploadBatchId(null);
                                    setFileList([]);
                                }}
                                size="large"
                            >
                                Cancel
                            </Button>
                        </div>

                        {uploadResult && (
                            <div style={{ marginTop: '24px', borderTop: '1px solid #f0f0f0', paddingTop: '20px' }}>
                                <h4 style={{ marginBottom: '16px' }}>
                                    <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
                                    Upload Summary
                                </h4>
                                <Row gutter={[16, 16]}>
                                    <Col span={8}>
                                        <Card bordered={false} style={{ background: '#fafafa', borderRadius: '8px' }}>
                                            <Statistic title="Total Records" value={uploadResult.total} valueStyle={{ color: '#1890ff' }} />
                                        </Card>
                                    </Col>
                                    <Col span={8}>
                                        <Card bordered={false} style={{ background: '#f6ffed', borderRadius: '8px' }}>
                                            <Statistic title="Success" value={uploadResult.success} valueStyle={{ color: '#52c41a' }} prefix={<CheckCircleOutlined />} />
                                        </Card>
                                    </Col>
                                    <Col span={8}>
                                        <Card bordered={false} style={{ background: '#fff2f0', borderRadius: '8px' }}>
                                            <Statistic title="Failed" value={uploadResult.failed} valueStyle={{ color: '#ff4d4f' }} prefix={<CloseCircleOutlined />} />
                                        </Card>
                                    </Col>
                                </Row>

                                {uploadBatchId && (
                                    <div style={{ marginTop: '16px', textAlign: 'center' }}>
                                        <Button
                                            type="primary"
                                            onClick={exportUploadBatch}
                                            icon={<FileExcelOutlined />}
                                            size="large"
                                            style={{ background: '#52c41a', borderColor: '#52c41a' }}
                                        >
                                            Download Result File
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </Modal>

            {/* Manual Refund Modal */}
            <Modal
                title="Manual Refund"
                visible={refundModalVisible}
                onOk={handleManualRefund}
                onCancel={() => setRefundModalVisible(false)}
                okText="Refund"
                cancelText="Cancel"
                confirmLoading={isLoading}
            >
                <p>Request ID: <strong>{refundRequestId}</strong></p>
                <div className="form-group">
                    <label>Remark (optional)</label>
                    <Input.TextArea
                        rows={3}
                        value={refundRemark}
                        onChange={(e) => setRefundRemark(e.target.value)}
                        placeholder="Enter refund remark"
                    />
                </div>
                <div className="alert alert-warning">
                    <strong>Warning:</strong> This will reverse commission and mark as refunded.
                </div>
            </Modal>

            {/* Bank Reference Number Modal */}
            <Modal
                title="Mark Success with Bank Reference"
                visible={bankRefModalVisible}
                onOk={handleBankRefSubmit}
                onCancel={() => setBankRefModalVisible(false)}
                okText="Submit"
                cancelText="Cancel"
                confirmLoading={isLoading}
            >
                <p>Request ID: <strong>{bankRefRequestId}</strong></p>
                <div className="form-group">
                    <label>Bank Reference Number (UTR/RRN) <span className="text-danger">*</span></label>
                    <Input
                        value={bankRefNumber}
                        onChange={(e) => setBankRefNumber(e.target.value)}
                        placeholder="Enter UTR or RRN"
                    />
                </div>
                <div className="form-group">
                    <label>Remark (optional)</label>
                    <Input.TextArea
                        rows={2}
                        value={bankRefRemark}
                        onChange={(e) => setBankRefRemark(e.target.value)}
                        placeholder="Optional remark"
                    />
                </div>
                <div className="alert alert-info">
                    This will mark the transaction as <strong>Success</strong> and store the bank reference number.
                </div>
            </Modal>

            {/* Mark Failed Modal */}
            <Modal
                title="Mark Transaction as Failed"
                visible={failModalVisible}
                onOk={handleFailSubmit}
                onCancel={() => setFailModalVisible(false)}
                okText="Confirm"
                cancelText="Cancel"
                confirmLoading={isLoading}
                >
                <p>Request ID: <strong>{failRequestId}</strong></p>
                <div className="form-group">
                    <label>Remark (optional)</label>
                    <Input.TextArea
                        rows={3}
                        value={failRemark}
                        onChange={(e) => setFailRemark(e.target.value)}
                        placeholder="Enter reason for failure"
                    />
                </div>
                <div className="alert alert-danger">
                    <strong>Warning:</strong> This will mark the transaction as <strong>Failed</strong> and reverse any commissions.
                </div>
            </Modal>
        </div>
    );
}

export default FundTransferAdminReport;